import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Drawer,
  Input,
  Popover,
  Progress,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  DeleteOutlined,
  BulbOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { route } from '../../../../../constants/routes';
import {
  CandidateMatchChatResponse,
  CandidateMatchCriteria,
  CandidateMatchResponse,
  CandidateMatchResult,
} from '../../candidate-match/models/candidate-match.model';
import { CandidateMatchService } from '../../candidate-match/services/candidate-match.service';
import './RecruiterCopilot.css';

const { TextArea } = Input;
const { Text } = Typography;

export interface RecruiterCopilotContext {
  key: string;
  label: string;
  detail: string;
  positionTitle?: string;
  entityType?: 'JOB' | 'JOB_APPLICATION';
  entityId?: string;
}

interface RecruiterCopilotProps {
  companyId?: string;
  context: RecruiterCopilotContext;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  response?: CandidateMatchChatResponse;
  error?: boolean;
}

interface RouteState {
  jobName?: string;
  candidateName?: string;
  clientName?: string;
}

export interface RecruiterCopilotHint {
  title: string;
  description: string;
  prompt: string;
}

const ASSISTANT_PAGE_SIZE = 5;

export function getRecruiterCopilotHints(
  context: RecruiterCopilotContext
): RecruiterCopilotHint[] {
  const hints: RecruiterCopilotHint[] = [];
  if (context.entityType) {
    hints.push({
      title: 'Use the current page',
      description:
        'Talent Assistant can use the job description and saved skills from this context.',
      prompt:
        context.entityType === 'JOB'
          ? 'Find candidates for this job'
          : 'Find candidates for this position',
    });
  }
  hints.push(
    {
      title: 'Describe the ideal profile',
      description:
        'Include the seniority, role, and the capabilities that matter most.',
      prompt:
        'Find a senior backend engineer with Java, Spring Boot, AWS, and REST API experience',
    },
    {
      title: 'Require experience evidence',
      description:
        'Ask for a minimum number of years. Results only include candidates whose résumé has dated, relevant experience.',
      prompt:
        'Find backend engineers with Java and at least 5 years of experience',
    },
    {
      title: 'Refine the previous results',
      description:
        'Add or remove skills in a follow-up without starting the search again.',
      prompt: 'Remove AWS and add Kubernetes',
    },
    {
      title: 'Replace the criteria',
      description:
        'Use “only” when you want a fresh, narrower set of requirements.',
      prompt: 'Only candidates with React and TypeScript',
    }
  );
  return hints;
}

export function getRecruiterCopilotContext(
  pathname: string,
  state?: RouteState | null
): RecruiterCopilotContext {
  const applicationMatch = pathname.match(
    /\/job-applications\/([0-9a-f-]{36})(?:\/|$)/i
  );
  if (applicationMatch) {
    return {
      key: pathname,
      label: 'Job application',
      detail: state?.jobName || state?.candidateName || 'Current application',
      positionTitle: state?.jobName,
      entityType: 'JOB_APPLICATION',
      entityId: applicationMatch[1],
    };
  }
  const jobMatch = pathname.match(/\/job\/([0-9a-f-]{36})(?:\/|$)/i);
  if (jobMatch) {
    return {
      key: pathname,
      label: 'Job',
      detail: state?.jobName || 'Current job',
      positionTitle: state?.jobName,
      entityType: 'JOB',
      entityId: jobMatch[1],
    };
  }
  if (pathname.includes('/candidate-profile')) {
    return {
      key: pathname,
      label: 'Candidate',
      detail: state?.candidateName || 'Current candidate',
    };
  }
  if (pathname.includes('/company')) {
    return {
      key: pathname,
      label: 'Company',
      detail: state?.clientName || 'Talent pool',
    };
  }
  if (pathname.includes('/interviews')) {
    return {
      key: pathname,
      label: 'Interview Agenda',
      detail: 'Current schedule',
    };
  }
  if (pathname.includes('/candidate-match')) {
    return {
      key: pathname,
      label: 'Candidate Match',
      detail: 'Full search view',
    };
  }
  if (pathname.endsWith('/job-applications')) {
    return {
      key: pathname,
      label: 'Job Applications',
      detail: 'Application pipeline',
    };
  }
  if (pathname.endsWith('/candidates')) {
    return {
      key: pathname,
      label: 'Candidates',
      detail: 'Company talent pool',
    };
  }
  if (pathname.endsWith('/job')) {
    return { key: pathname, label: 'Jobs', detail: 'Company job portfolio' };
  }
  if (pathname.endsWith('/dashboard')) {
    return { key: pathname, label: 'Dashboard', detail: 'Talent operations' };
  }
  return { key: pathname, label: 'Workspace', detail: 'Company talent pool' };
}

function initials(candidate: CandidateMatchResult): string {
  return `${candidate.firstname?.[0] ?? ''}${candidate.lastname?.[0] ?? ''}`.toUpperCase();
}

function fitColor(score: number): string {
  if (score >= 75) return '#16875d';
  if (score >= 45) return '#d18a00';
  return '#d04a43';
}

export function getCandidatePagination(match: CandidateMatchResponse) {
  const size = Math.max(match.size || 10, 1);
  const totalElements = Math.max(match.totalElements, 0);
  const totalPages =
    match.totalPages ?? Math.ceil(totalElements / Math.max(size, 1));
  const page = Math.min(
    Math.max(match.page ?? 0, 0),
    Math.max(totalPages - 1, 0)
  );

  return {
    page,
    size,
    totalPages,
    start: totalElements ? page * size + 1 : 0,
    end: totalElements ? Math.min((page + 1) * size, totalElements) : 0,
  };
}

const RecruiterCopilot: React.FC<RecruiterCopilotProps> = ({
  companyId,
  context,
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [contextEnabled, setContextEnabled] = useState(true);
  const [refiningMessageId, setRefiningMessageId] = useState<string | null>(
    null
  );
  const [pagingMessageId, setPagingMessageId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setContextEnabled(true);
  }, [context.key]);

  useEffect(() => {
    if (!open) {
      setHelpOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    });
  }, [messages, open, sending]);

  const latestCriteria = useMemo(
    () =>
      [...messages]
        .reverse()
        .find((item) => item.role === 'assistant' && item.response?.criteria)
        ?.response?.criteria,
    [messages]
  );
  const hints = useMemo(() => getRecruiterCopilotHints(context), [context]);

  const suggestedPrompts = [
    'Find a senior backend engineer with Java and at least 5 years of experience',
    'Show frontend candidates with React and TypeScript',
    'Find DevOps profiles with Kubernetes and Azure',
  ];

  const applyHint = (prompt: string) => {
    setDraft(prompt);
    setHelpOpen(false);
  };

  const submitMessage = async (value = draft) => {
    const message = value.trim();
    if (!message || sending) return;
    if (!companyId) {
      setMessages((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content:
            'Your company profile is still loading. Try again in a moment.',
          error: true,
        },
      ]);
      return;
    }

    const userMessage: CopilotMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: message,
    };
    setMessages((current) => [...current, userMessage]);
    setDraft('');
    setSending(true);
    try {
      const response = await CandidateMatchService.Chat(companyId, {
        message,
        contextTitle: contextEnabled ? context.positionTitle : undefined,
        contextType: contextEnabled ? context.entityType : undefined,
        contextId: contextEnabled ? context.entityId : undefined,
        previousPosition: latestCriteria?.position,
        previousSkills: latestCriteria?.skills,
        previousMinimumYearsExperience: latestCriteria?.minimumYearsExperience,
        limit: ASSISTANT_PAGE_SIZE,
        page: 0,
      });
      setMessages((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response.assistantMessage,
          response,
        },
      ]);
    } catch (error) {
      console.error('Talent Assistant query failed:', error);
      setMessages((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content:
            'I could not search the talent pool right now. Your message is still here so you can try again.',
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const updateSkills = async (
    messageId: string,
    criteria: CandidateMatchCriteria,
    nextSkills: string[]
  ) => {
    if (!companyId || !nextSkills.length) return;
    setRefiningMessageId(messageId);
    try {
      const candidateMatch = await CandidateMatchService.Search(companyId, {
        position: criteria.position,
        description: criteria.description,
        skills: nextSkills,
        minimumYearsExperience: criteria.minimumYearsExperience,
        limit: ASSISTANT_PAGE_SIZE,
        page: 0,
      });
      setMessages((current) =>
        current.map((item) =>
          item.id === messageId && item.response
            ? {
                ...item,
                content: `Updated the ranking using ${nextSkills.length} skill${
                  nextSkills.length === 1 ? '' : 's'
                }.`,
                response: {
                  ...item.response,
                  criteria: { ...criteria, skills: nextSkills },
                  candidateMatch,
                },
              }
            : item
        )
      );
    } catch (error) {
      console.error('Talent Assistant refinement failed:', error);
    } finally {
      setRefiningMessageId(null);
    }
  };

  const changePage = async (
    messageId: string,
    criteria: CandidateMatchCriteria,
    page: number
  ) => {
    if (!companyId || page < 0) return;
    setPagingMessageId(messageId);
    try {
      const candidateMatch = await CandidateMatchService.Search(companyId, {
        position: criteria.position,
        description: criteria.description,
        skills: criteria.skills,
        minimumYearsExperience: criteria.minimumYearsExperience,
        limit: ASSISTANT_PAGE_SIZE,
        page,
      });
      setMessages((current) =>
        current.map((item) =>
          item.id === messageId && item.response
            ? {
                ...item,
                response: {
                  ...item.response,
                  candidateMatch,
                },
              }
            : item
        )
      );
    } catch (error) {
      console.error('Talent Assistant pagination failed:', error);
    } finally {
      setPagingMessageId(null);
    }
  };

  const viewCandidate = (candidate: CandidateMatchResult) => {
    onOpenChange(false);
    navigate(route(`/candidate-profile/${candidate.candidateId}`), {
      state: {
        candidateName: `${candidate.firstname} ${candidate.lastname}`.trim(),
        fromCandidateMatch: true,
      },
    });
  };

  return (
    <>
      {!open ? (
        <Tooltip title="Ask Talent Assistant" placement="left">
          <button
            type="button"
            className="recruiter-copilot-trigger"
            aria-label="Open Talent Assistant"
            onClick={() => onOpenChange(true)}
          >
            <RobotOutlined />
            <span>Assistant</span>
          </button>
        </Tooltip>
      ) : null}

      <Drawer
        className={`recruiter-copilot-drawer${
          expanded ? ' recruiter-copilot-drawer--expanded' : ''
        }`}
        width={expanded ? '100vw' : 'min(100vw, 500px)'}
        open={open}
        onClose={() => onOpenChange(false)}
        closable={false}
        title={
          <div className="recruiter-copilot-title">
            <span className="recruiter-copilot-title__mark">
              <BulbOutlined />
            </span>
            <div>
              <strong>Talent Assistant</strong>
              <span>AI-assisted candidate search</span>
            </div>
          </div>
        }
        extra={
          <div className="recruiter-copilot-title__actions">
            <Popover
              placement="bottomRight"
              trigger="click"
              open={helpOpen}
              onOpenChange={setHelpOpen}
              overlayClassName="recruiter-copilot-help-popover"
              content={
                <div className="recruiter-copilot-help">
                  <div className="recruiter-copilot-help__intro">
                    <span>
                      <RobotOutlined />
                    </span>
                    <div>
                      <strong>How to ask Talent Assistant</strong>
                      <p>
                        Start naturally, then refine the same search with
                        follow-up messages.
                      </p>
                    </div>
                  </div>
                  <div className="recruiter-copilot-help__steps">
                    {hints.map((hint, index) => (
                      <button
                        type="button"
                        key={hint.title}
                        onClick={() => applyHint(hint.prompt)}
                      >
                        <span>{index + 1}</span>
                        <div>
                          <strong>{hint.title}</strong>
                          <p>{hint.description}</p>
                          <code>“{hint.prompt}”</code>
                        </div>
                        <ArrowRightOutlined />
                      </button>
                    ))}
                  </div>
                  <div className="recruiter-copilot-help__shortcut">
                    <span>Enter</span> sends · <span>Shift + Enter</span> adds a
                    new line
                  </div>
                </div>
              }
            >
              <Button
                className="recruiter-copilot-help-trigger"
                type="text"
                icon={<QuestionCircleOutlined />}
              >
                How to use
              </Button>
            </Popover>
            <Tooltip title={expanded ? 'Restore chat' : 'Expand chat'}>
              <Button
                type="text"
                icon={
                  expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />
                }
                aria-label={expanded ? 'Restore chat' : 'Expand chat'}
                onClick={() => setExpanded((current) => !current)}
              />
            </Tooltip>
            {messages.length ? (
              <Button
                type="text"
                icon={<DeleteOutlined />}
                aria-label="Clear conversation"
                onClick={() => setMessages([])}
              />
            ) : null}
            <Button
              type="text"
              icon={<CloseOutlined />}
              aria-label="Close Talent Assistant"
              onClick={() => onOpenChange(false)}
            />
          </div>
        }
        styles={{ body: { padding: 0 } }}
      >
        <div className="recruiter-copilot-shell">
          <div className="recruiter-copilot-context">
            <span>Using page context</span>
            {contextEnabled ? (
              <Tag
                closable
                onClose={() => setContextEnabled(false)}
                icon={<BulbOutlined />}
              >
                {context.label}: {context.detail}
              </Tag>
            ) : (
              <Button type="link" onClick={() => setContextEnabled(true)}>
                Add current page
              </Button>
            )}
          </div>

          <div className="recruiter-copilot-messages" ref={scrollRef}>
            {!messages.length ? (
              <section className="recruiter-copilot-welcome">
                <span className="recruiter-copilot-welcome__icon">
                  <RobotOutlined />
                </span>
                <h2>Who are you looking for?</h2>
                <p>
                  Describe the role naturally. I’ll turn it into visible skill
                  criteria and rank candidates using evidence in their profiles.
                </p>
                <div className="recruiter-copilot-prompts">
                  {suggestedPrompts.map((prompt) => (
                    <button
                      type="button"
                      key={prompt}
                      onClick={() => void submitMessage(prompt)}
                    >
                      <BulbOutlined />
                      <span>{prompt}</span>
                      <ArrowRightOutlined />
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {messages.map((item) => (
              <article
                key={item.id}
                className={`recruiter-copilot-message recruiter-copilot-message--${item.role}`}
              >
                <Avatar
                  className="recruiter-copilot-message__avatar"
                  icon={
                    item.role === 'assistant' ? (
                      <RobotOutlined />
                    ) : (
                      <UserOutlined />
                    )
                  }
                />
                <div className="recruiter-copilot-message__body">
                  <div
                    className={`recruiter-copilot-bubble${
                      item.error ? ' recruiter-copilot-bubble--error' : ''
                    }`}
                  >
                    {item.content}
                  </div>

                  {item.response?.criteria &&
                  (item.response.criteria.skills.length ||
                    item.response.criteria.minimumYearsExperience != null) ? (
                    <div className="recruiter-copilot-criteria">
                      <div>
                        <span>Search criteria</span>
                        <small>Refine with a follow-up or remove a skill</small>
                      </div>
                      <strong>{item.response.criteria.position}</strong>
                      <div className="recruiter-copilot-criteria__skills">
                        {item.response.criteria.minimumYearsExperience !=
                        null ? (
                          <Tag className="recruiter-copilot-criteria__experience">
                            {item.response.criteria.minimumYearsExperience}+
                            years · dated résumé
                          </Tag>
                        ) : null}
                        {item.response.criteria.skills.map((skill) => (
                          <Tag
                            key={skill}
                            closable={item.response!.criteria.skills.length > 1}
                            onClose={(event) => {
                              event.preventDefault();
                              void updateSkills(
                                item.id,
                                item.response!.criteria,
                                item.response!.criteria.skills.filter(
                                  (itemSkill) => itemSkill !== skill
                                )
                              );
                            }}
                          >
                            {skill}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {refiningMessageId === item.id ? (
                    <div className="recruiter-copilot-refining">
                      <span />
                      Updating candidate ranking…
                    </div>
                  ) : null}

                  {item.response?.candidateMatch ? (
                    <div className="recruiter-copilot-results">
                      <div className="recruiter-copilot-results__summary">
                        <div>
                          <strong>
                            {item.response.candidateMatch.totalElements} match
                            {item.response.candidateMatch.totalElements === 1
                              ? ''
                              : 'es'}
                          </strong>
                          <span>
                            Ranked by requested skills
                            {item.response.criteria.minimumYearsExperience !=
                            null
                              ? ' and dated résumé experience'
                              : ''}
                          </span>
                        </div>
                        <span className="recruiter-copilot-results__shown">
                          Showing{' '}
                          {
                            getCandidatePagination(item.response.candidateMatch)
                              .start
                          }
                          –
                          {
                            getCandidatePagination(item.response.candidateMatch)
                              .end
                          }
                        </span>
                      </div>

                      {item.response.candidateMatch.unresolvedSkills.length ? (
                        <Alert
                          type="warning"
                          showIcon
                          message={`Not found in skill catalog: ${item.response.candidateMatch.unresolvedSkills.join(
                            ', '
                          )}`}
                        />
                      ) : null}

                      {item.response.criteria.minimumYearsExperience != null &&
                      item.response.candidateMatch.candidates.some(
                        (candidate) => !candidate.experienceVerified
                      ) ? (
                        <Alert
                          type="info"
                          showIcon
                          message="Some matching profiles need experience verification"
                          description="They match the requested skills and position, but their résumé does not contain enough dated evidence to confirm the minimum years."
                        />
                      ) : null}

                      <div className="recruiter-copilot-results__list">
                        {item.response.candidateMatch.candidates.map(
                          (candidate) => (
                            <button
                              type="button"
                              className="recruiter-copilot-candidate"
                              key={candidate.candidateId}
                              onClick={() => viewCandidate(candidate)}
                            >
                              <Avatar>{initials(candidate)}</Avatar>
                              <div className="recruiter-copilot-candidate__main">
                                <strong>
                                  {candidate.firstname} {candidate.lastname}
                                </strong>
                                <span>
                                  {candidate.inferredPosition ||
                                    'Candidate profile'}
                                </span>
                                <div>
                                  {candidate.experienceVerified &&
                                  candidate.relevantYearsExperience != null ? (
                                    <Tag className="recruiter-copilot-candidate__experience">
                                      {candidate.relevantYearsExperience} years
                                    </Tag>
                                  ) : item.response!.criteria
                                      .minimumYearsExperience != null ? (
                                    <Tag className="recruiter-copilot-candidate__experience recruiter-copilot-candidate__experience--unverified">
                                      Years not verified
                                    </Tag>
                                  ) : null}
                                  {candidate.matchedSkills.map((skill) => (
                                    <Tag key={skill}>{skill}</Tag>
                                  ))}
                                </div>
                                <div className="recruiter-copilot-candidate__details">
                                  <span>{candidate.email}</span>
                                  {candidate.phoneNumber ? (
                                    <span>{candidate.phoneNumber}</span>
                                  ) : null}
                                  <span>
                                    {candidate.matchedSkillCount}/
                                    {candidate.totalSkillCount} matched skills
                                  </span>
                                  {candidate.missingSkills.length ? (
                                    <span>
                                      Missing:{' '}
                                      {candidate.missingSkills.join(', ')}
                                    </span>
                                  ) : (
                                    <span>All requested skills found</span>
                                  )}
                                  {candidate.positionConfidence != null ? (
                                    <span>
                                      {candidate.positionConfidence}% position
                                      confidence
                                    </span>
                                  ) : null}
                                  {candidate.positionRationale ? (
                                    <p>{candidate.positionRationale}</p>
                                  ) : null}
                                  {candidate.experienceEvidence ? (
                                    <p>
                                      Experience evidence:{' '}
                                      {candidate.experienceEvidence}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                              <div className="recruiter-copilot-candidate__score">
                                <strong>{candidate.skillFitScore}%</strong>
                                <Progress
                                  percent={candidate.skillFitScore}
                                  showInfo={false}
                                  strokeColor={fitColor(
                                    candidate.skillFitScore
                                  )}
                                  size="small"
                                />
                                <span>skill fit</span>
                              </div>
                            </button>
                          )
                        )}
                      </div>

                      {!item.response.candidateMatch.candidates.length ? (
                        <div className="recruiter-copilot-no-results">
                          Try a broader skill set or ask a new question.
                        </div>
                      ) : null}

                      {getCandidatePagination(item.response.candidateMatch)
                        .totalPages > 1 ? (
                        <div className="recruiter-copilot-pagination">
                          <Button
                            icon={<ArrowLeftOutlined />}
                            disabled={
                              getCandidatePagination(
                                item.response.candidateMatch
                              ).page === 0 || pagingMessageId === item.id
                            }
                            onClick={() =>
                              void changePage(
                                item.id,
                                item.response!.criteria,
                                getCandidatePagination(
                                  item.response!.candidateMatch!
                                ).page - 1
                              )
                            }
                          >
                            Previous
                          </Button>
                          <span>
                            Page{' '}
                            {getCandidatePagination(
                              item.response.candidateMatch
                            ).page + 1}{' '}
                            of{' '}
                            {
                              getCandidatePagination(
                                item.response.candidateMatch
                              ).totalPages
                            }
                          </span>
                          <Button
                            icon={<ArrowRightOutlined />}
                            iconPosition="end"
                            loading={pagingMessageId === item.id}
                            disabled={
                              getCandidatePagination(
                                item.response.candidateMatch
                              ).page >=
                                getCandidatePagination(
                                  item.response.candidateMatch
                                ).totalPages -
                                  1 || pagingMessageId === item.id
                            }
                            onClick={() =>
                              void changePage(
                                item.id,
                                item.response!.criteria,
                                getCandidatePagination(
                                  item.response!.candidateMatch!
                                ).page + 1
                              )
                            }
                          >
                            Next
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </article>
            ))}

            {sending ? (
              <article className="recruiter-copilot-message recruiter-copilot-message--assistant">
                <Avatar
                  className="recruiter-copilot-message__avatar"
                  icon={<RobotOutlined />}
                />
                <div className="recruiter-copilot-thinking">
                  <span />
                  <span />
                  <span />
                  Searching your talent pool
                </div>
              </article>
            ) : null}
          </div>

          <footer className="recruiter-copilot-composer">
            <div className="recruiter-copilot-composer__box">
              <TextArea
                value={draft}
                autoSize={{ minRows: 2, maxRows: 5 }}
                maxLength={50000}
                placeholder="Describe the candidate profile you need…"
                disabled={sending}
                onChange={(event) => setDraft(event.target.value)}
                onPressEnter={(event) => {
                  if (!event.shiftKey) {
                    event.preventDefault();
                    void submitMessage();
                  }
                }}
              />
              <Button
                type="primary"
                shape="circle"
                icon={<SendOutlined />}
                disabled={!draft.trim()}
                loading={sending}
                aria-label="Send message"
                onClick={() => void submitMessage()}
              />
            </div>
            <Text type="secondary">
              Talent Assistant ranks profile evidence; recruiters make the final
              decision.
            </Text>
          </footer>
        </div>
      </Drawer>
    </>
  );
};

export default RecruiterCopilot;
